import * as React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import { Form } from 'react-final-form';
import Button from '@mui/material/Button';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import AutoRenewIcon from '@mui/icons-material/Autorenew';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import RadarIcon from '@mui/icons-material/Radar';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { setColorSchemeMode } from 'infrastructure/reducers/config';
import { TextInput } from 'components/Inputs';
import validators from 'utils/validators';
import useClient from 'infrastructure/hooks/useClient';
import useConnector from 'infrastructure/hooks/useConnector';

const LANGUAGES_LABEL = [
  {
    code: 'en',
    i18nKey: 'english',
  },
  {
    code: 'nl',
    i18nKey: 'dutch',
  },
  {
    code: 'fr',
    i18nKey: 'french',
  },
];

// Predefined connector port list
const CONNECTOR_PRESETS = [
  { port: 51983, label: 'Trust1Connector' },
  { port: 52183, label: 'KeysignConnector' },
  { port: 53183, label: 'BelfiusConnector' },
  { port: 54983, label: 'LimburgConnector' },
  { port: 54383, label: 'CrossuiteConnector' },
];

const Heading = styled(Typography)(({ theme }) => ({
  margin: '20px 0 10px',
  color: theme.palette.grey[600],
  fontWeight: 700,
  fontSize: theme.typography.pxToRem(11),
  textTransform: 'uppercase',
  letterSpacing: '.08rem',
}));

const IconToggleButton = styled(ToggleButton)({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  '& > *': {
    marginRight: '8px',
  },
});

const scanPort = async (baseUrl, port) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 2000);
  try {
    await fetch(`${baseUrl}:${port}/v3/info`, {
      signal: controller.signal,
      mode: 'no-cors',
    });
    clearTimeout(timeout);
    return true;
  } catch (e) {
    clearTimeout(timeout);
    if (e.name === 'AbortError') return false;
    // A TypeError saying "Failed to fetch" typically means connection refused
    if (e instanceof TypeError && e.message.includes('Failed to fetch')) return false;
    // Any other error (e.g. CORS block on an actual response) means the server is reachable
    return true;
  }
};

const AppSettingsDrawer = (props) => {
  const { getConnectorState, setConnectorState, resetConnectorState } = useClient();
  const { init } = useConnector();
  const { enqueueSnackbar } = useSnackbar();
  const mode = useSelector((state) => state.config.colorSchemeMode);
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const [scanning, setScanning] = React.useState(false);
  const [foundConnectors, setFoundConnectors] = React.useState([]);

  const { onClose, open = false, ...other } = props;

  // Reset scan results when drawer closes
  React.useEffect(() => {
    if (!open) {
      setFoundConnectors([]);
      setScanning(false);
    }
  }, [open]);

  const handleChangeThemeMode = (event, paletteMode) => {
    if (paletteMode === null) {
      return;
    }

    dispatch(setColorSchemeMode(paletteMode));
  };

  const handleLanguageClick = (language) => () => {
    i18n.changeLanguage(language.code);
    moment.locale(language.code);
    onClose();
  };

  const reset = () => {
    resetConnectorState();
    let s = getConnectorState();
    init(s.url, s.port);
    enqueueSnackbar(t('notification.info.connectorReinitialized', 'Trust1Connector re-initialized!'), {
      variant: 'info',
    });
    onClose();
  };

  const onSubmit = ({ url, port }) => {
    let s = getConnectorState();
    s.url = url;
    s.port = port;
    setConnectorState(s);
    init(url, port);
    enqueueSnackbar(t('notification.info.connectorReinitialized', 'Trust1Connector re-initialized!'), {
      variant: 'info',
    });
    onClose();
  };

  const handleScan = async () => {
    setScanning(true);
    setFoundConnectors([]);
    const state = getConnectorState();
    const baseUrl = state.url || 'https://localhost';

    const results = await Promise.all(
      CONNECTOR_PRESETS.map(async (preset) => {
        const found = await scanPort(baseUrl, preset.port);
        return found ? preset : null;
      }),
    );

    const found = results.filter(Boolean);
    setFoundConnectors(found);
    setScanning(false);

    if (found.length === 0) {
      enqueueSnackbar(t('notification.info.noConnectorsFound', 'No connectors found on predefined ports.'), {
        variant: 'warning',
      });
    }
  };

  const handleSelectPreset = (preset, form) => {
    const state = getConnectorState();
    form.change('url', state.url || 'https://localhost');
    form.change('port', String(preset.port));
  };

  return (
    <Drawer
      anchor="right"
      onClose={onClose}
      open={open}
      PaperProps={{
        elevation: 0,
        sx: { width: { xs: 310, sm: 360 }, borderRadius: '10px 0px 0px 10px' },
      }}
      {...other}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
        <Typography variant="body1" fontWeight="500">
          {t('field.settings', 'Settings')}
        </Typography>
        <IconButton color="inherit" onClick={onClose} edge="end">
          <CloseIcon color="primary" fontSize="small" />
        </IconButton>
      </Box>
      <Divider />
      <Box sx={{ pl: 2, pr: 2 }}>
        <Heading gutterBottom id="settings-mode">
          {t('settings.theme.mode', 'Mode')}
        </Heading>
        <ToggleButtonGroup
          exclusive
          value={mode}
          color="primary"
          onChange={handleChangeThemeMode}
          aria-labelledby={t('settings.theme.mode', 'Mode')}
          fullWidth
        >
          <IconToggleButton value="light" aria-label={t('settings.theme.light', 'Light')}>
            <LightModeIcon fontSize="small" />
            {t('settings.theme.light', 'Light')}
          </IconToggleButton>
          <IconToggleButton value="system" aria-label={t('settings.theme.system', 'System')}>
            <SettingsBrightnessIcon fontSize="small" />
            {t('settings.theme.system', 'System')}
          </IconToggleButton>
          <IconToggleButton value="dark" aria-label={t('settings.theme.dark', 'Dark')}>
            <DarkModeOutlinedIcon fontSize="small" />
            {t('settings.theme.dark', 'Dark')}
          </IconToggleButton>
        </ToggleButtonGroup>
        <Heading gutterBottom>{t('field.language', 'Language')}</Heading>
        <List>
          {LANGUAGES_LABEL.map((language) => (
            <ListItemButton
              component="a"
              divider
              key={language.code}
              onClick={handleLanguageClick(language)}
              selected={i18n.language === language.code}
              lang={language.code}
              hrefLang={language.code}
            >
              {t(`locale.${language.i18nKey}`)}
            </ListItemButton>
          ))}
        </List>
        <Heading gutterBottom>{t('field.trust1connector', 'Trust1Connector')}</Heading>
        <Form className="page-contact__form" onSubmit={onSubmit} initialValues={getConnectorState()}>
          {({ handleSubmit, form }) => (
            <form onSubmit={handleSubmit}>
              <Stack spacing={1}>
                <TextInput
                  label={t('field.url', 'Url')}
                  name="url"
                  size="small"
                  margin="dense"
                  validate={validators.isRequired}
                />
                <TextInput
                  label={t('field.port', 'Port')}
                  name="port"
                  size="small"
                  margin="dense"
                  validate={validators.isRequired}
                />
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Button
                  type="submit"
                  className="connector-submit"
                  size="small"
                  color="primary"
                  variant="contained"
                  startIcon={<AutoRenewIcon />}
                >
                  {t('action.reInitialize', 'Re-initialize')}
                </Button>
                <Button
                  className="connector-reset"
                  size="small"
                  color="primary"
                  variant="outlined"
                  onClick={reset}
                  startIcon={<RemoveCircleOutlineIcon />}
                >
                  {t('action.reset', 'Reset')}
                </Button>
              </Stack>
              <Divider sx={{ mt: 1, mb: 1 }} />
              <Heading gutterBottom>{t('settings.connector.scan', 'Scan for connectors')}</Heading>
              <Button
                size="small"
                color="secondary"
                variant="outlined"
                onClick={handleScan}
                disabled={scanning}
                startIcon={scanning ? <CircularProgress size={14} /> : <RadarIcon />}
                fullWidth
                sx={{ mb: 1 }}
              >
                {scanning
                  ? t('settings.connector.scanning', 'Scanning...')
                  : t('settings.connector.scanPorts', 'Scan predefined ports')}
              </Button>
              {foundConnectors.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {t('settings.connector.foundConnectors', 'Found connectors — click to use as preset:')}
                  </Typography>
                  <List dense disablePadding sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                    {foundConnectors.map((preset) => (
                      <ListItemButton
                        key={preset.port}
                        divider
                        onClick={() => handleSelectPreset(preset, form)}
                        sx={{ py: 0.5 }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleOutlineIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={preset.label}
                          secondary={`port ${preset.port}`}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </>
              )}
            </form>
          )}
        </Form>
      </Box>
    </Drawer>
  );
};

AppSettingsDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool,
};

export default AppSettingsDrawer;
